// Copyright (c) Wiens Consulting. All rights reserved.

import { useEffect, useState } from "react"
import { IconDeviceFloppy, IconLoader2, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react"
import { ApiError, api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import { parseProgramSettingsDraft } from "./program-settings"

interface ProgramYearSettings {
  revision: number
  startDate: string
  calculationConstants: {
    coefficientA: number
    coefficientB: number
    coefficientC: number
    maximumBenefit: number
    minimumBenefit: number
  }
  eligibilityGuidelines: {
    householdSize: number
    monthlyIncomeLimit: number
  }[]
  householdSizeAdjusters: {
    numberOfPeople: number
    adjustmentAmount: number
  }[]
  housingTypeAdjusters: {
    housingType: number
    adjustmentAmount: number
  }[]
  backupEnergyCosts: {
    energySourceId: number
    energySourceName: string
    amount: number
  }[]
}

interface GuidelineRow {
  householdSize: string
  monthlyIncomeLimit: string
}

interface ProgramSettingsEditorProps {
  programId: number
  programYear: number
  settings: ProgramYearSettings | null
  canEdit: boolean
  onSaved: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function initialDate(settings: ProgramYearSettings | null): string {
  return settings?.startDate ? settings.startDate.slice(0, 10) : ""
}

function initialRows(settings: ProgramYearSettings | null): GuidelineRow[] {
  return settings?.eligibilityGuidelines.map((guideline) => ({
    householdSize: String(guideline.householdSize),
    monthlyIncomeLimit: String(guideline.monthlyIncomeLimit),
  })) ?? []
}

interface NumberRow { numberOfPeople: string; adjustmentAmount: string }
interface HousingRow { housingType: string; adjustmentAmount: string }
interface BackupRow { energySourceId: string; energySourceName: string; amount: string }

/** Edits only the supported, admin-owned portion of a unified program-year revision. */
export function ProgramSettingsEditor({
  programId,
  programYear,
  settings,
  canEdit,
  onSaved,
  open: controlledOpen,
  onOpenChange,
}: ProgramSettingsEditorProps) {
  const [localOpen, setLocalOpen] = useState(false)
  const [startDate, setStartDate] = useState(() => initialDate(settings))
  const [coefficientA, setCoefficientA] = useState(() => String(settings?.calculationConstants.coefficientA ?? ""))
  const [coefficientB, setCoefficientB] = useState(() => String(settings?.calculationConstants.coefficientB ?? ""))
  const [coefficientC, setCoefficientC] = useState(() => String(settings?.calculationConstants.coefficientC ?? ""))
  const [minimumBenefit, setMinimumBenefit] = useState(() => String(settings?.calculationConstants.minimumBenefit ?? ""))
  const [maximumBenefit, setMaximumBenefit] = useState(() => String(settings?.calculationConstants.maximumBenefit ?? ""))
  const [guidelines, setGuidelines] = useState<GuidelineRow[]>(() => initialRows(settings))
  const [householdSizeAdjusters, setHouseholdSizeAdjusters] = useState<NumberRow[]>(() => settings?.householdSizeAdjusters.map((row) => ({ numberOfPeople: String(row.numberOfPeople), adjustmentAmount: String(row.adjustmentAmount) })) ?? [])
  const [housingTypeAdjusters, setHousingTypeAdjusters] = useState<HousingRow[]>(() => settings?.housingTypeAdjusters.map((row) => ({ housingType: String(row.housingType), adjustmentAmount: String(row.adjustmentAmount) })) ?? [])
  const [backupEnergyCosts, setBackupEnergyCosts] = useState<BackupRow[]>(() => settings?.backupEnergyCosts.map((row) => ({ energySourceId: String(row.energySourceId), energySourceName: row.energySourceName, amount: String(row.amount) })) ?? [])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [editTarget, setEditTarget] = useState<{ programId: number; programYear: number; expectedRevision: number | null } | null>(null)
  const registerEditor = useBenefitEditRegistration()

  const open = controlledOpen ?? localOpen
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setLocalOpen(next)
    onOpenChange?.(next)
  }

  // Freeze both the target and the loaded revision for the lifetime of the dialog. A parent
  // reload or a year change must not reset a draft or cause Save to address a different year.
  useEffect(() => {
    if (!open) return
    setEditTarget({ programId, programYear, expectedRevision: settings?.revision ?? null })
    setStartDate(initialDate(settings))
    setCoefficientA(String(settings?.calculationConstants.coefficientA ?? ""))
    setCoefficientB(String(settings?.calculationConstants.coefficientB ?? ""))
    setCoefficientC(String(settings?.calculationConstants.coefficientC ?? ""))
    setMinimumBenefit(String(settings?.calculationConstants.minimumBenefit ?? ""))
    setMaximumBenefit(String(settings?.calculationConstants.maximumBenefit ?? ""))
    setGuidelines(initialRows(settings))
    setHouseholdSizeAdjusters(settings?.householdSizeAdjusters.map((row) => ({ numberOfPeople: String(row.numberOfPeople), adjustmentAmount: String(row.adjustmentAmount) })) ?? [])
    setHousingTypeAdjusters(settings?.housingTypeAdjusters.map((row) => ({ housingType: String(row.housingType), adjustmentAmount: String(row.adjustmentAmount) })) ?? [])
    setBackupEnergyCosts(settings?.backupEnergyCosts.map((row) => ({ energySourceId: String(row.energySourceId), energySourceName: row.energySourceName, amount: String(row.amount) })) ?? [])
    setError(null)
  // Deliberately only reset on an open transition; settings is a live parent prop and may reload
  // while this dialog contains unsaved values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    registerEditor(open)
  }, [open, registerEditor])

  if (!canEdit) return null

  const parseNumber = (value: string, label: string): number => {
    if (value.trim() === "") throw new Error(`${label} is required.`)
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) throw new Error(`${label} must be a valid number.`)
    return parsed
  }

  const save = async () => {
    setError(null)
    try {
      if (!editTarget) throw new Error("The settings target is no longer available. Close and reopen the editor.")
      const parsedDraft = parseProgramSettingsDraft({ startDate, guidelines, minimumBenefit, maximumBenefit })
      const parsedGuidelines = parsedDraft.guidelines
      const parsedMinimum = parsedDraft.minimumBenefit
      const parsedMaximum = parsedDraft.maximumBenefit
      if (editTarget.expectedRevision !== null && parsedGuidelines.length === 0) {
        throw new Error("A calculation revision must include at least one eligibility guideline.")
      }
      const parsedHouseholdSizeAdjusters = householdSizeAdjusters.map((row) => ({
        numberOfPeople: parseNumber(row.numberOfPeople, "Household size"),
        adjustmentAmount: parseNumber(row.adjustmentAmount, "Household size adjustment"),
      }))
      const parsedHousingTypeAdjusters = housingTypeAdjusters.map((row) => ({
        housingType: parseNumber(row.housingType, "Housing type"),
        adjustmentAmount: parseNumber(row.adjustmentAmount, "Housing type adjustment"),
      }))
      const parsedBackupEnergyCosts = backupEnergyCosts.map((row) => ({
        energySourceId: parseNumber(row.energySourceId, "Energy source"),
        energySourceName: row.energySourceName,
        amount: parseNumber(row.amount, "Backup energy cost"),
      }))
      if (editTarget.expectedRevision !== null && (parsedHouseholdSizeAdjusters.length === 0 || parsedHousingTypeAdjusters.length === 0 || parsedBackupEnergyCosts.length === 0)) {
        throw new Error("A calculation revision must include all supported adjusters and backup energy costs.")
      }

      await api.put(
        `/api/benefit-programs/${editTarget.programId}/settings`,
        {
          programYear: editTarget.programYear,
          expectedRevision: editTarget.expectedRevision,
          startDate: `${startDate}T00:00:00Z`,
          calculationConstants: {
            coefficientA: parseNumber(coefficientA, "Coefficient A"),
            coefficientB: parseNumber(coefficientB, "Coefficient B"),
            coefficientC: parseNumber(coefficientC, "Coefficient C"),
            minimumBenefit: parsedMinimum,
            maximumBenefit: parsedMaximum,
          },
          ...(parsedGuidelines.length > 0 ? { eligibilityGuidelines: parsedGuidelines } : {}),
          ...(parsedHouseholdSizeAdjusters.length > 0 ? { householdSizeAdjusters: parsedHouseholdSizeAdjusters } : {}),
          ...(parsedHousingTypeAdjusters.length > 0 ? { housingTypeAdjusters: parsedHousingTypeAdjusters } : {}),
          ...(parsedBackupEnergyCosts.length > 0 ? { backupEnergyCosts: parsedBackupEnergyCosts } : {}),
        },
      )
      toast.success(settings ? "Program settings published" : "Program year created")
      setOpen(false)
      onSaved()
    } catch (err) {
      const message = err instanceof ApiError && err.status === 409
        ? "These settings changed in another session. Reload the year and review it before saving again."
        : err instanceof Error ? err.message : "Failed to save program settings"
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const updateGuideline = (index: number, field: keyof GuidelineRow, value: string) => {
    setGuidelines((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row))
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            <IconPencil className="mr-1.5 size-4" />
            Edit settings
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{settings ? "Publish program settings" : "Create program year"}</DialogTitle>
          <DialogDescription>
            Program year {programYear}. Saving publishes a new revision; prior revisions remain available to historical calculations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">{error}</div>}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`start-date-${programId}-${programYear}`}>Start date</Label>
              <Input id={`start-date-${programId}-${programYear}`} type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </div>
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Calculation constants</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {[
                ["Coefficient A", coefficientA, setCoefficientA],
                ["Coefficient B", coefficientB, setCoefficientB],
                ["Coefficient C", coefficientC, setCoefficientC],
                ["Minimum benefit", minimumBenefit, setMinimumBenefit],
                ["Maximum benefit", maximumBenefit, setMaximumBenefit],
              ].map(([label, value, setter]) => {
                const fieldId = `${String(label).toLowerCase().replaceAll(" ", "-")}-${programId}-${programYear}`
                return (
                  <div className="space-y-2" key={label as string}>
                    <Label htmlFor={fieldId}>{label as string}</Label>
                    <Input id={fieldId} type="number" step="any" value={value as string} onChange={(event) => (setter as (value: string) => void)(event.target.value)} />
                  </div>
                )
              })}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold">Eligibility guidelines</h3>
                <p className="text-xs text-muted-foreground">Household sizes must be unique and range from 0 through 24.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setGuidelines((current) => [...current, { householdSize: "", monthlyIncomeLimit: "" }])}>
                <IconPlus className="mr-1.5 size-4" />
                Add row
              </Button>
            </div>
            {guidelines.length === 0 ? (
              <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">No guideline rows. Add at least one for formula-based programs.</p>
            ) : (
              <div className="space-y-2">
                {guidelines.map((row, index) => (
                  <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2" key={index}>
                    <div className="space-y-1">
                      <Label className="text-xs">Household size</Label>
                      <Input aria-label={`Household size for guideline ${index + 1}`} type="number" min="0" max="24" step="1" value={row.householdSize} onChange={(event) => updateGuideline(index, "householdSize", event.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Monthly income limit</Label>
                      <Input aria-label={`Monthly income limit for guideline ${index + 1}`} type="number" min="0" step="0.01" value={row.monthlyIncomeLimit} onChange={(event) => updateGuideline(index, "monthlyIncomeLimit", event.target.value)} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Remove guideline row ${index + 1}`} onClick={() => setGuidelines((current) => current.filter((_, rowIndex) => rowIndex !== index))}>
                      <IconTrash className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold">Household-size adjusters</h3>
                <p className="text-xs text-muted-foreground">These values are consumed by the eligibility calculator.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setHouseholdSizeAdjusters((current) => [...current, { numberOfPeople: "", adjustmentAmount: "" }])}>
                <IconPlus className="mr-1.5 size-4" /> Add row
              </Button>
            </div>
            <div className="space-y-2">
              {householdSizeAdjusters.map((row, index) => (
                <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2" key={index}>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`people-${programId}-${programYear}-${index}`}>Number of people</Label><Input id={`people-${programId}-${programYear}-${index}`} type="number" min="0" max="24" step="1" value={row.numberOfPeople} onChange={(event) => setHouseholdSizeAdjusters((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, numberOfPeople: event.target.value } : item))} /></div>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`people-adjustment-${programId}-${programYear}-${index}`}>Adjustment amount</Label><Input id={`people-adjustment-${programId}-${programYear}-${index}`} type="number" min="0" step="any" value={row.adjustmentAmount} onChange={(event) => setHouseholdSizeAdjusters((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, adjustmentAmount: event.target.value } : item))} /></div>
                  <Button type="button" variant="ghost" size="icon" aria-label={`Remove household-size adjuster ${index + 1}`} onClick={() => setHouseholdSizeAdjusters((current) => current.filter((_, rowIndex) => rowIndex !== index))}><IconTrash className="size-4" /></Button>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div><h3 className="text-sm font-semibold">Housing-type adjusters</h3><p className="text-xs text-muted-foreground">Use the canonical housing type value shown in the program data.</p></div>
              <Button type="button" size="sm" variant="outline" onClick={() => setHousingTypeAdjusters((current) => [...current, { housingType: "", adjustmentAmount: "" }])}><IconPlus className="mr-1.5 size-4" /> Add row</Button>
            </div>
            <div className="space-y-2">
              {housingTypeAdjusters.map((row, index) => (
                <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2" key={index}>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`housing-type-${programId}-${programYear}-${index}`}>Housing type value</Label><Input id={`housing-type-${programId}-${programYear}-${index}`} type="number" min="0" max={row.housingType === "255" ? 255 : 5} step="1" value={row.housingType} onChange={(event) => setHousingTypeAdjusters((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, housingType: event.target.value } : item))} /></div>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`housing-adjustment-${programId}-${programYear}-${index}`}>Adjustment amount</Label><Input id={`housing-adjustment-${programId}-${programYear}-${index}`} type="number" min="0" step="any" value={row.adjustmentAmount} onChange={(event) => setHousingTypeAdjusters((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, adjustmentAmount: event.target.value } : item))} /></div>
                  <Button type="button" variant="ghost" size="icon" aria-label={`Remove housing-type adjuster ${index + 1}`} onClick={() => setHousingTypeAdjusters((current) => current.filter((_, rowIndex) => rowIndex !== index))}><IconTrash className="size-4" /></Button>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2"><div><h3 className="text-sm font-semibold">Backup energy costs</h3><p className="text-xs text-muted-foreground">{programId === 0
              ? "PSE IDs: 0 all electric; 1 gas and electric; 2 gas only; 3 electric base; 4 electric portion; 5 gas portion. Names are descriptive; the ID determines calculation behavior."
              : "Energy source IDs map to the provider-specific calculation table."}</p></div><Button type="button" size="sm" variant="outline" onClick={() => setBackupEnergyCosts((current) => [...current, { energySourceId: "", energySourceName: "", amount: "" }])}><IconPlus className="mr-1.5 size-4" /> Add row</Button></div>
            <div className="space-y-2">
              {backupEnergyCosts.map((row, index) => (
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2" key={index}>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`energy-source-${programId}-${programYear}-${index}`}>Energy source ID</Label><Input id={`energy-source-${programId}-${programYear}-${index}`} type="number" min="0" step="1" value={row.energySourceId} onChange={(event) => setBackupEnergyCosts((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, energySourceId: event.target.value } : item))} /></div>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`energy-name-${programId}-${programYear}-${index}`}>Energy source name</Label><Input id={`energy-name-${programId}-${programYear}-${index}`} value={row.energySourceName} onChange={(event) => setBackupEnergyCosts((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, energySourceName: event.target.value } : item))} /></div>
                  <div className="space-y-1"><Label className="text-xs" htmlFor={`energy-amount-${programId}-${programYear}-${index}`}>Amount</Label><Input id={`energy-amount-${programId}-${programYear}-${index}`} type="number" min="0" step="0.01" value={row.amount} onChange={(event) => setBackupEnergyCosts((current) => current.map((item, rowIndex) => rowIndex === index ? { ...item, amount: event.target.value } : item))} /></div>
                  <Button type="button" variant="ghost" size="icon" aria-label={`Remove backup energy cost ${index + 1}`} onClick={() => setBackupEnergyCosts((current) => current.filter((_, rowIndex) => rowIndex !== index))}><IconTrash className="size-4" /></Button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={() => { setSaving(true); void save() }} disabled={saving}>
            {saving ? <IconLoader2 className="mr-1.5 size-4 animate-spin" /> : <IconDeviceFloppy className="mr-1.5 size-4" />}
            Publish revision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
